import type { Session } from '@supabase/supabase-js'

const API_BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:8000') + '/v1'

export async function apiFetch<T>(
  path: string,
  session: Session,
  options: RequestInit = {},
  timeoutMs = 30_000,
): Promise<T> {
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`,
  }

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)

  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, { ...options, headers, signal: controller.signal })
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      const err = new Error('La richiesta ha impiegato troppo tempo. Controlla la connessione e riprova.') as Error & { status: number; isTimeout: boolean }
      err.status = 0
      err.isTimeout = true
      throw err
    }
    throw e
  } finally {
    clearTimeout(timer)
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    const err = new Error(error.detail ?? 'Request failed') as Error & { status: number }
    err.status = response.status
    throw err
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export interface Alert {
  id: string
  name: string
  filters: Record<string, unknown>
  active: boolean
  created_at: string
  match_count: number
}

export interface Posting {
  id: string
  inpa_id: string
  title: string
  body: string | null
  deadline: string | null
  locations: string[] | null
  sector: string | null
  category: string | null
  url: string
  scraped_at: string
  published_at: string | null
  ente: string | null
  positions: number | null
  role: string | null
  status: 'OPEN' | 'CLOSED'
}

export interface MatchesPage {
  items: Posting[]
  total: number
  page: number
  size: number
  has_more: boolean
}

export interface FilterOptions {
  categories: string[]
  sectors: string[]
  regions: string[]
}

export const filtersApi = {
  get: (session: Session) =>
    apiFetch<FilterOptions>('/filters', session),
  searchEnti: (q: string, session: Session) =>
    apiFetch<string[]>(`/enti?q=${encodeURIComponent(q)}`, session),
}

export interface SavedPosting extends Posting {
  saved_at: string
}

export const savedPostingsApi = {
  listIds: (session: Session) =>
    apiFetch<string[]>('/saved-postings/ids', session),
  list: (session: Session) =>
    apiFetch<SavedPosting[]>('/saved-postings', session),
  save: (postingId: string, session: Session) =>
    apiFetch<{ message: string }>(`/saved-postings/${postingId}`, session, { method: 'POST' }),
  unsave: (postingId: string, session: Session) =>
    apiFetch<{ message: string }>(`/saved-postings/${postingId}`, session, { method: 'DELETE' }),
}

export const alertsApi = {
  list: (session: Session) =>
    apiFetch<Alert[]>('/alerts', session),
  getLimit: (session: Session) =>
    apiFetch<{ limit: number; count: number }>('/alerts/limit', session),
  create: (name: string, filters: Record<string, unknown>, session: Session) =>
    apiFetch<Alert>('/alerts', session, {
      method: 'POST',
      body: JSON.stringify({ name, filters }),
    }),
  delete: (id: string, session: Session) =>
    apiFetch<void>(`/alerts/${id}`, session, { method: 'DELETE' }),
  update: (id: string, name: string, filters: Record<string, unknown>, session: Session) =>
    apiFetch<Alert>(`/alerts/${id}`, session, {
      method: 'PATCH',
      body: JSON.stringify({ name, filters }),
    }, 60_000),
  getMatches: (alertId: string, session: Session, page = 0, size = 20) =>
    apiFetch<MatchesPage>(`/alerts/${alertId}/matches?page=${page}&size=${size}`, session),
}